"use client";

import type {
	FestivalDetails,
	FestivalReviewData,
	RatingCategory,
} from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import { Textarea } from "@workspace/ui/components/textarea";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { ReviewStars } from "@/components/features/festival/review/review-stars";
import { useRealtimeFestivalReview } from "@/hooks/features/festival/use-realtime-festival-review";
import { useCurrentUser } from "@/stores";

interface CategoryRatingProps {
	id: string;
	label: string;
	description?: string;
	rating: number;
	onChange: (value: number) => void;
}

const CategoryRating = ({
	id,
	label,
	description,
	rating,
	onChange,
}: CategoryRatingProps) => {
	return (
		<div className="space-y-1">
			<Label htmlFor={id}>{label}</Label>
			{description && (
				<p className="text-muted-foreground text-xs">{description}</p>
			)}
			<ReviewStars rating={rating} onChange={onChange} />
		</div>
	);
};

// Rating categories configuration
const RATING_CATEGORIES: RatingCategory[] = [
	{
		id: "vibeRating",
		label: "Vibe",
		description: "Overall atmosphere, energy, and crowd",
	},
	{
		id: "productionRating",
		label: "Event Production",
		description: "Sound quality, visuals, and stage design",
	},
	{
		id: "logisticsRating",
		label: "Logistics & Facilities",
		description: "Parking, transportation, lines and bathroom",
	},
	{
		id: "foodRating",
		label: "Food, Drinks & Activities",
		description: "Overall value, quality and variety",
	},
];

// Helper to determine if review data is empty (would trigger deletion)
// Matches server logic: checks if there's ANY content
function isEmptyReviewData(reviewData: FestivalReviewData): boolean {
	const hasOverallRating = reviewData.overallRating > 0;
	const hasSubRatings =
		reviewData.vibeRating > 0 ||
		reviewData.productionRating > 0 ||
		reviewData.logisticsRating > 0 ||
		reviewData.foodRating > 0;
	const hasContent = reviewData.content && reviewData.content.trim().length > 0;

	return !hasOverallRating && !hasSubRatings && !hasContent;
}

interface FestivalReviewModalProps {
	festival: FestivalDetails | null;
	isOpen: boolean;
	onClose: () => void;
	onSaved?: () => void; // Optional callback after save
}

export const FestivalReviewModal = memo(
	({ festival, isOpen, onClose, onSaved }: FestivalReviewModalProps) => {
		const currentUser = useCurrentUser();
		const isMountedRef = useRef(true);

		useEffect(() => {
			isMountedRef.current = true;
			return () => {
				isMountedRef.current = false;
			};
		}, []);

		// Enhanced onClose handler with cleanup guard
		const handleClose = () => {
			if (isMountedRef.current) {
				onClose();
			}
		};

		// Local state for form inputs
		const [localOverallRating, setLocalOverallRating] = useState(0);
		const [localVibeRating, setLocalVibeRating] = useState(0);
		const [localProductionRating, setLocalProductionRating] = useState(0);
		const [localLogisticsRating, setLocalLogisticsRating] = useState(0);
		const [localFoodRating, setLocalFoodRating] = useState(0);
		const [localContent, setLocalContent] = useState("");

		// Use real-time hook for instant updates
		const { review, saveReview, isPending } = useRealtimeFestivalReview({
			festivalId: festival?.id || "",
			userId: currentUser?.id || null,
			enabled: !!currentUser?.id && !!festival?.id && isOpen,
		});

		// Track if we had a review when the modal opened (for button text logic)
		const hadInitialReviewRef = useRef(false);
		// Track if form has been initialized for this modal open
		const formInitializedRef = useRef(false);

		// Initialize form ONCE when modal opens, using the best available data.
		// This prevents realtime updates from overwriting in-progress user edits.
		// Only lock the form (formInitializedRef=true) once we have real data,
		// so the null→data transition from the initial fetch can still populate the form.
		useEffect(() => {
			if (isOpen && !formInitializedRef.current) {
				hadInitialReviewRef.current = !!review;

				if (review) {
					// Got real data — initialize and lock the form
					formInitializedRef.current = true;
					setLocalOverallRating(review.overallRating || 0);
					setLocalVibeRating(review.vibeRating || 0);
					setLocalProductionRating(review.productionRating || 0);
					setLocalLogisticsRating(review.logisticsRating || 0);
					setLocalFoodRating(review.foodRating || 0);
					setLocalContent(review.content || "");
				} else {
					// No data yet — set defaults but keep unlocked for data to arrive
					setLocalOverallRating(0);
					setLocalVibeRating(0);
					setLocalProductionRating(0);
					setLocalLogisticsRating(0);
					setLocalFoodRating(0);
					setLocalContent("");
				}
			} else if (!isOpen) {
				formInitializedRef.current = false;
			}
		}, [isOpen, review]);

		// Handle save - matches original design where save handles both update and remove
		const handleSave = useCallback(async () => {
			if (!currentUser || !festival?.id) return;

			// Create review data object to check if empty
			const reviewData: FestivalReviewData = {
				festivalId: festival.id,
				overallRating: localOverallRating,
				vibeRating: localVibeRating,
				productionRating: localProductionRating,
				logisticsRating: localLogisticsRating,
				foodRating: localFoodRating,
				content: localContent,
			};

			// When deleting (all fields empty), send all ratings as -2 to signal deletion
			const isEmpty = isEmptyReviewData(reviewData);

			await saveReview({
				overallRating: isEmpty ? -2 : localOverallRating,
				vibeRating: isEmpty ? -2 : localVibeRating,
				productionRating: isEmpty ? -2 : localProductionRating,
				logisticsRating: isEmpty ? -2 : localLogisticsRating,
				foodRating: isEmpty ? -2 : localFoodRating,
				content: localContent,
			});

			onClose();
			onSaved?.();
		}, [
			currentUser,
			festival?.id,
			localOverallRating,
			localVibeRating,
			localProductionRating,
			localLogisticsRating,
			localFoodRating,
			localContent,
			saveReview,
			onClose,
			onSaved,
		]);

		// Create a review data object from local state to check if it's empty
		const localReviewData: FestivalReviewData = {
			festivalId: festival?.id || "",
			overallRating: localOverallRating,
			vibeRating: localVibeRating,
			productionRating: localProductionRating,
			logisticsRating: localLogisticsRating,
			foodRating: localFoodRating,
			content: localContent,
		};

		const isLocalReviewEmpty = isEmptyReviewData(localReviewData);

		if (!festival) {
			return null;
		}

		return (
			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md md:max-w-lg">
					<DialogTitle className="sr-only">Review Festival</DialogTitle>
					<DialogDescription className="sr-only">
						Rate and review this festival. Share your overall experience and add
						personal notes.
					</DialogDescription>
					<DialogHeader className="space-y-4 px-6 pt-6 pb-0">
						<h2 className="font-semibold leading-none tracking-tight">
							Review {festival.name}
						</h2>
					</DialogHeader>

					<div className="space-y-4 px-6">
						{/* Overall Rating */}
						<div className="flex flex-col space-y-2 border-muted border-b pb-4">
							<Label>Overall Rating</Label>
							<ReviewStars
								rating={localOverallRating}
								onChange={(value: number) => setLocalOverallRating(value)}
							/>
						</div>

						{/* Category Ratings */}
						<div className="space-y-2">
							{RATING_CATEGORIES.map((category) => (
								<CategoryRating
									key={category.id}
									id={category.id}
									label={category.label}
									description={category.description}
									rating={
										category.id === "vibeRating"
											? localVibeRating
											: category.id === "productionRating"
												? localProductionRating
												: category.id === "logisticsRating"
													? localLogisticsRating
													: localFoodRating
									}
									onChange={(value) => {
										if (category.id === "vibeRating") setLocalVibeRating(value);
										else if (category.id === "productionRating")
											setLocalProductionRating(value);
										else if (category.id === "logisticsRating")
											setLocalLogisticsRating(value);
										else if (category.id === "foodRating")
											setLocalFoodRating(value);
									}}
								/>
							))}
						</div>

						{/* Review text */}
						<div>
							<Textarea
								id="review-content"
								placeholder="Write your review here..."
								className="min-h-[100px]"
								value={localContent}
								onChange={(e) => setLocalContent(e.target.value)}
							/>
						</div>
					</div>

					<DialogFooter className="px-6 pt-0 pb-6">
						<div className="flex w-full items-center justify-between">
							<div /> {/* Spacer for alignment */}
							<div className="flex gap-2">
								<Button
									variant="ghost"
									onClick={handleClose}
									disabled={isPending}
								>
									Cancel
								</Button>
								<Button
									onClick={handleSave}
									disabled={isPending}
									variant={
										isLocalReviewEmpty && hadInitialReviewRef.current
											? "destructive"
											: "default"
									}
								>
									{isLocalReviewEmpty && hadInitialReviewRef.current
										? "Remove"
										: hadInitialReviewRef.current
											? "Update"
											: "Save"}
								</Button>
							</div>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	},
);

FestivalReviewModal.displayName = "FestivalReviewModal";
